import axios from "axios";
import swal from "sweetalert";
import Vue from "vue";
import i18n from "../i18n";

export default {
  state: {
    page: "selectPlan",
    stripeOptions: null,
    plan: null,
    plans: null,
    pricingPeriod: "monthly",
    licenceCount: 1,
    learners: [],
    annualLearners: [],
    discount: {},
    paymentProcessing: null,
    showPlans: false,
    subscriptionId: null,
    billingAddress: null,
    currentSubscription: null,
    triggerInactiveSubscriptionModal: null,
    updatedBilling: null,
    restarted: null,
  },
  mutations: {
    setPage(state, page) {
      state.page = page;
    },
    setCurrentSubscription(state, currentSubscription) {
      state.currentSubscription = currentSubscription;
      // Set state to reflect current plan
      state.pricingPeriod =
        currentSubscription.interval == "year" ? "annual" : "monthly";
      state.learners = currentSubscription.learners;
      state.annualLearners = currentSubscription.annualLearners;
      state.licenceCount = currentSubscription.seats;
    },
    clearSubscriptionOptions(state) {
      state.currentSubscription = null;
      state.pricingPeriod = "monthly";
      state.learners = [];
      state.annualLearners = [];
      state.licenceCount = 1;
      state.page = "selectPlan";
      state.discount = {};
      (state.plans = null), (state.plan = null), (state.updatedBilling = null);
    },
    setStripeOptions(state, stripeOptions) {
      state.stripeOptions = stripeOptions;
    },
    setPlan(state, plan) {
      state.plan = plan;
      recordViewItem(state);
    },
    setUpdatedBilling(state, updatedBilling) {
      state.updatedBilling = updatedBilling;
    },
    setPlans(state, plans) {
      state.plans = plans;
    },
    setPricingPeriod(state, pricingPeriod) {
      state.pricingPeriod = pricingPeriod;
      recordViewItem(state);
    },
    setLicenceCount(state, licenceCount) {
      state.licenceCount = licenceCount;
    },
    setLearners(state, learners) {
      state.learners = learners;
      recordViewItem(state);
    },
    setAnnualLearners(state, annualLearners) {
      state.annualLearners = annualLearners;
    },
    setDiscount(state, discount) {
      state.discount = discount;
    },
    setPaymentProcessing(state, paymentProcessing) {
      state.paymentProcessing = paymentProcessing;
    },
    setShowPlans(state, showPlans) {
      state.showPlans = showPlans;
    },
    setSubscriptionId(state, id) {
      state.subscriptionId = id;
    },
    setBillingAddress(state, address) {
      state.billingAddress = address;
    },
    setTriggerInactiveSubscriptionModal(state, visible) {
      state.triggerInactiveSubscriptionModal = visible;
    },
    setSubscriptionRestarted(state, restarted) {
      state.restarted = restarted;
    },
  },
  getters: {
    updateNow(state, getters) {
      if (!state.currentSubscription) {
        return;
      }
      // Update now when going from month to year with a plan upgrade or no plan change OR when upgrading plan with no interval change
      return (
        (getters.upgradingInterval &&
          (getters.upgradingPlan || !getters.downgradingPlan)) ||
        (getters.upgradingPlan &&
          (!getters.upgradingInterval && !getters.downgradingInterval))
      );
    },
    upgradingInterval(state) {
      let interval = state.pricingPeriod == "annual" ? "year" : "month";
      return (
        state.currentSubscription.interval == "month" && interval == "year"
      );
    },
    upgradingPlan(state) {
      return state.plan.sort_order > state.currentSubscription.plan.sort_order;
    },
    downgradingInterval(state) {
      let interval = state.pricingPeriod == "annual" ? "year" : "month";
      return (
        state.currentSubscription.interval == "year" && interval == "month"
      );
    },
    downgradingPlan(state) {
      return state.plan.sort_order < state.currentSubscription.plan.sort_order;
    },
    cancelScheduled(state) {
      if (
        !state.currentSubscription ||
        !state.currentSubscription.renewal_plan
      ) {
        return false;
      } else {
        let interval = state.pricingPeriod == "annual" ? "year" : "month";
        return (
          state.plan.id == state.currentSubscription.plan.id &&
          state.currentSubscription.interval == interval
        );
      }
    },
    proRataDiscount(state, getters) {
      let discountedPrice = Math.floor(
        getters.unitPriceWithProRataDiscount * getters.seats * 100
      );
      return getters.unitPrice * getters.seats - discountedPrice / 100;
    },
    proviso(state) {
      if (
        state.pricingPeriod === "monthly" &&
        state.plan &&
        state.plan.monthly_proviso
      ) {
        return state.plan.monthly_proviso;
      }
      if (
        state.pricingPeriod === "annual" &&
        state.plan &&
        state.plan.annual_proviso
      ) {
        return state.plan.annual_proviso;
      }
      return null;
    },
    noChoice(state) {
      // return true if only one plan, and it has only a single renewal interval available
      // or single plan with no features listed
      if (state.plans && state.plans.length === 1) {
        if (!(state.plan.monthly_price && state.plan.annual_price)) {
          return true;
        }
        if (state.plans[0].features.length === 0) {
          return true;
        }
      }
      return false;
    },
    maxUsers(state) {
      return (
        (state.discount && state.discount.max_users) || state.plan.max_users
      );
    },
    minUsers(state) {
      return state.plan.min_users;
    },
    unitPrice(state) {
      if (state.plan) {
        return state.plan[state.pricingPeriod + "_price"] / 100;
      } else {
        return 0;
      }
    },
    discountActive(state, getters) {
      if (getters.cancelScheduled) {
        return false;
      }
      return !(
        getters.unitPrice >= 0 &&
        state.discount &&
        Object.keys(state.discount)
      )
        ? false
        : (state.discount.applies_to === "both" ||
            state.discount.applies_to === state.pricingPeriod) &&
            (!state.discount.required_subscription_plan_id ||
              state.discount.required_subscription_plan_id === state.plan.id);
    },
    discountFailureReason(state, getters) {
      if (!getters.discountActive) {
        let discount = state.discount;
        if (
          discount.required_subscription_plan_id &&
          discount.required_subscription_plan_id !== state.plan.id
        ) {
          return i18n.t("message.not-valid-for-this-plan");
        }
        if (discount.applies_to === "monthly") {
          return i18n.t("message.monthly-plans-only");
        }
        if (discount.applies_to === "annual") {
          return i18n.t("message.annual-plans-only");
        }
      }
    },
    // Unit price after discount code and/or pro rata discount
    discountedUnitPrice(state, getters) {
      let discount = 0;
      if (state.discount && getters.discountActive) {
        if (state.discount.amount_reduced) {
          discount = state.discount.amount_reduced / 100;
        } else if (state.discount.percentage_reduced) {
          discount =
            (getters.unitPrice * state.discount.percentage_reduced) / 100;
        }
      }
      let unitPrice = getters.unitPrice;
      if (getters.updateNow) {
        unitPrice =
          state.plan[state.pricingPeriod + "_pro_rata_discounted_price"] / 100;
      }
      return Math.max(Math.floor(unitPrice - discount), 0);
    },
    // Unit price with discount code only (does not take pro rata discount into account)
    unitPriceWithDiscountCode(state, getters) {
      let discount = 0;
      if (state.discount) {
        if (state.discount.amount_reduced) {
          discount = state.discount.amount_reduced / 100;
        } else if (state.discount.percentage_reduced) {
          discount =
            (getters.unitPrice * state.discount.percentage_reduced) / 100;
        }
      }
      return Math.max(Math.floor(getters.unitPrice - discount), 0);
    },
    // Unit price with pro rata discount only
    unitPriceWithProRataDiscount(state, getters) {
      return Math.max(
        Math.floor(
          state.plan[state.pricingPeriod + "_pro_rata_discounted_price"] / 100
        )
      );
    },
    seats(state) {
      return state.pricingPeriod === "monthly"
        ? state.learners.length
        : state.licenceCount;
    },
    subscriptionRestarted(state) {
      return state.restarted;
    },
    totalPrice(state, getters) {
      return getters.unitPrice * getters.seats;
    },
    finalPrice(state, getters) {
      if (getters.cancelScheduled) {
        return 0;
      }
      if (getters.discountActive || getters.updateNow) {
        // discountUnitPrice takes proRata discount into account
        return getters.discountedUnitPrice * getters.seats * 100;
      } else if (getters.cancelScheduled) {
        // User is cancelling a scheduled plan change - no charge
        return 0;
      } else {
        return getters.totalPrice * 100;
      }
    },
    plan(state) {
      return state.plan;
    },
    pricingPeriod(state) {
      return state.pricingPeriod;
    },
  },
  actions: {
    tryDiscountCode({ state, commit, dispatch }, token) {
      return axios
        .get(`/payment/discount_code/${token}`)
        .then((response) => {
          commit("setDiscount", response.data);
        })
        .catch((error) => {
          swal("Error", error.response.data.error.description, "error");
        });
    },
    async finalisePayment({ state, getters, commit, dispatch }) {
      try {
        let resp = await axios.post("/payment/finalise", {
          total: getters.finalPrice,
          address: state.billingAddress,
          current_subscription: state.currentSubscription
            ? state.currentSubscription.id
            : null,
        });

        commit("setPaymentProcessing", false);
        commit("setUserHasSubscription");
        commit("setSubscriptionId", resp.data.subscription_id);
        commit("setUpdatedBilling", resp.data.billing);
        if (
          state.pricingPeriod === "annual" &&
          getters.seats > 1 &&
          !state.currentSubscription
        ) {
          commit("setPage", "addLearners");
        } else {
          commit("setPage", "thanks");
        }
        try {
          if (window.dataLayer) {
            dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            dataLayer.push({
              event: "eec.purchase",
              ecommerce: {
                purchase: {
                  actionField: {
                    id: state.subscriptionId,
                    revenue: getters.finalPrice / 100,
                    coupon: state.discount ? state.discount.token : null,
                  },
                  products: [
                    {
                      name: state.plan.name,
                      id: state.plan.id,
                      price: getters.unitPriceWithDiscountCode,
                      variant: state.pricingPeriod,
                      quantity: getters.seats,
                      coupon: state.discount ? state.discount.token : null,
                    },
                  ],
                },
              },
            });
          }
        } catch (e) {}

        await dispatch("clearPaymentRedirect");
      } catch (err) {
        let message =
          err.response && err.response.data.error
            ? err.response.data.error.description
            : "Unable to complete payment";
        swal("Error", message, "error");
        commit("setPaymentProcessing", false);
        console.error(err);
      }
    },
    clearPaymentRedirect({ commit, getters }) {
      // If we were sent here by a force redirect, clear it
      if (getters.forceRedirect && getters.forceRedirect.match(/^\/payment/)) {
        commit("setRedirect", null);
      }
    },
    purchaseProgram({ commit, state, rootState }, program) {
      if (!program.payment_required) {
        return;
      }
      let handler = StripeCheckout.configure({
        key: program.payment_details.stripe_key,
        image: "/static/dashboard/core/img/roundlogo_programs.png",
        color: "black",
        token(token) {
          let url = `/programs/${program.id}/buy`;
          let data = new URLSearchParams();
          data.append("token", token.id);
          data.append("form_token", program.payment_details.csrf_token);
          data.append("price", program.payment_details.price);

          axios
            .post(url, data)
            .then((resp) => {
              program.payment_required = false;
              location.assign(resp.data.url);
            })
            .catch((err) => {
              let message;
              try {
                message = err.response.data.message;
              } catch (e) {
                message = "Unable to process payment at the time";
              }
              swal("Error", message, "error");
            });
        },
      });

      handler.open({
        name: "Nomadic",
        description: program.title,
        amount: program.payment_details.price,
        email: rootState.auth.user.email,
      });
    },
  },
};

function recordViewItem(state) {
  try {
    let seats =
      state.pricingPeriod === "monthly"
        ? state.learners.length
        : state.licenceCount;
    let unitPrice = state.plan[state.pricingPeriod + "_price"] / 100;
    if (window.dataLayer) {
      dataLayer.push({ ecommerce: null });
      dataLayer.push({
        event: "eec.impression",
        ecommerce: {
          currencyCode: "USD",
          impressions: [
            {
              name: state.plan.name,
              id: state.plan.id,
              price: unitPrice,
              variant: state.pricingPeriod,
            },
          ],
        },
      });
    }
  } catch (e) {}
}
